import React, { useMemo } from 'react';
import { ThemeSprites } from '@shared/utils/styleApplicator/ThemeSprites';
import { useThemeCanvas } from '@shared/utils/styleApplicator/useThemeCanvas';
import type { ApplicationContext } from '@shared/utils/styleApplicator/styleApplicator.types';
import type { GuestSiteEventDesign } from '../../../layout.types';
import { BRANNAN_FRAMES_MEDIA, brannanConfig, CustomCssGlobalStyle, useBrannanDesignValues } from '../../LayoutBrannan.theme';
import { BrannanFrameMaxWidth, FrameInner, FrameOuter } from '../Frames';
import { Page } from '../Page';
import type { PageTopPadding } from '../Page/Page';
import chroma from 'chroma-js';
import { useColorPaletteProvider } from '../../../ColorPaletteProvider/ColorPaletteProvider';
import { BrannanNavigation, DesignLayoutType } from '@graphql/generated-base';
import { getCustomArtUrl, getIsCustomArtEnabled } from '../../../layout.utils';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { addRendition } from '@shared/utils/photoRendition';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

const { layerBack, layerMiddle } = brannanConfig.applicatorHtmlProps;

const BACK_CONTEXT: ApplicationContext = { composition: 'stacked', layer: 'back' };
const MIDDLE_CONTEXT: ApplicationContext = { composition: 'stacked', layer: 'middle' };

// Each frame's distance from the viewport edge, stated in CSS so viewport-fixed sprites can be
// positioned during render and ship visible in the SSR markup (see ThemeSprites' frameInset).
// The outer frame is full-bleed; the inner one is centred and capped at BrannanFrameMaxWidth,
// which collapses to the outer frame's 0 below that width — exactly what max() expresses.
const BACK_FRAME_INSET = '0px';
const MIDDLE_FRAME_INSET = `max(0px, calc(50% - ${BrannanFrameMaxWidth / 2}px))`;

// Extracted for unit-testability — keeps the mobile/desktop asset selection and rendition logic in one place.
// `shouldHideArt` already carries the breakpoint decision; the caller owns which predicate that is.
export const getShellCustomArtProps = (eventDesign: GuestSiteEventDesign | undefined, isMobileOrTablet: boolean, shouldHideArt = false) => ({
  // The shell always uses the desktop theme asset (not the mobile one) so the body art matches the desktop layout.
  // PRNT-5076: the `hideDesktopArtOnMobile` service-center flag drops it on mobile instead, leaving art
  // only in the title banner — for couples whose desktop upload reads wrong beside the page content.
  isCustomArtEnabled: shouldHideArt ? false : getIsCustomArtEnabled(eventDesign),
  // On mobile we add rendition=medium to avoid downloading the full-res image.
  customArtUrl: isMobileOrTablet ? addRendition({ url: getCustomArtUrl(eventDesign), renditionSize: 'medium' }) : getCustomArtUrl(eventDesign, DesignLayoutType.brannan),
});

export interface ShellProps {
  eventDesign?: GuestSiteEventDesign;
  noTranslucency?: boolean;
  isAlohaGuestPage?: boolean;
  isCustomPage?: boolean;
  /** 'auto' (default) lets the layout decide; 'hidden' e.g. when a masthead should hug the top of the card. */
  topPadding?: PageTopPadding;
  /**
   * Loading state of the page-level useBrannanDesign() call. Shell never invokes the
   * style applicator itself — exactly one writer may paint the shared applicator
   * elements per page (PRNT-4856) — so the owning page passes its loading state down.
   * Omit for skeleton pages with no writer (e.g., RegistryShell during initial load).
   */
  styleApplicatorLoading?: boolean;
}

export const Shell: React.FC<ShellProps> = ({ eventDesign, isCustomPage, topPadding, styleApplicatorLoading = false, children }) => {
  const { applicatorConfig, customCss, isSmallScreen } = useBrannanDesignValues(eventDesign);
  const { pageColor, pageFontColor, artBackgroundColor } = useColorPaletteProvider();

  // PRNT-4898 bundled path: theme art as React. useThemeCanvas is inert (empty sprites, no
  // style) unless the websiteBundleStyleApplicator flag is affirmatively 'on', so the legacy
  // external applicator keeps sole ownership of these nodes with the flag off or unresolved.
  // Legacy parity: on small screens Brannan paints the title-banner component instead of the
  // stacked frames, so the frame canvases render nothing there.
  const themeId = eventDesign?.theme.themeId;
  const backCanvas = useThemeCanvas(isSmallScreen ? undefined : themeId, BACK_CONTEXT, applicatorConfig?.layerBack);
  const middleCanvas = useThemeCanvas(isSmallScreen ? undefined : themeId, MIDDLE_CONTEXT, applicatorConfig?.layerMiddle);

  const isSinglePage = eventDesign?.websiteLayout.layoutType === DesignLayoutType.brannan && eventDesign?.brannanNavigation === BrannanNavigation.singlePage;

  // For some themes (e.g. Rustic Chic) custom background color is not being applied even if enabled
  // This workaround allows to override the default theme color
  const customBackgroundColorConfig = applicatorConfig?.layerBack.customBackgroundColor;
  const customBackgroundColor =
    customBackgroundColorConfig?.isEnabled && customBackgroundColorConfig?.backgroundColor
      ? chroma(customBackgroundColorConfig?.backgroundColor as Array<number>).hex()
      : undefined;

  const customTextColor =
    customBackgroundColorConfig?.isEnabled && customBackgroundColorConfig?.textColor ? chroma(customBackgroundColorConfig?.textColor as Array<number>).hex() : undefined;

  const isMobileOrTablet = useIsMobileOrTablet();
  const [shouldHideDesktopArtOnMobile, isFlagLoading] = useIsFeatureEnabled({ eventFeatureFlagKey: 'hideDesktopArtOnMobile' });
  // Hide on Brannan's own sm4 (960px) breakpoint, not useIsMobileOrTablet (<1024px): EventNameNav
  // renders the title-banner art below sm4 too, so the wider predicate blanks the art entirely
  // between 960 and 1024 instead of leaving it in the title banner.
  // Also hold the art while the flag is unresolved — a flagged event would otherwise paint the
  // desktop art and yank it a tick later. CustomArt is position:fixed, so holding it shifts no layout.
  const shouldHideArt = isSmallScreen && (shouldHideDesktopArtOnMobile || isFlagLoading);
  const { isCustomArtEnabled, customArtUrl } = useMemo(() => getShellCustomArtProps(eventDesign, isMobileOrTablet, shouldHideArt), [eventDesign, isMobileOrTablet, shouldHideArt]);

  return (
    <FrameOuter
      loading={styleApplicatorLoading}
      applicatorProp={layerBack.prop}
      customBackgroundColor={customBackgroundColor}
      loadingBackgroundColor={artBackgroundColor}
      customTextColor={customTextColor}
      isCustomPage={isCustomPage}
      customArtUrl={customArtUrl}
      isCustomArtEnabled={isCustomArtEnabled}
      themeSprites={
        backCanvas.sprites.length > 0 ? (
          <ThemeSprites
            sprites={backCanvas.sprites}
            context={BACK_CONTEXT}
            frameInset={BACK_FRAME_INSET}
            media={BRANNAN_FRAMES_MEDIA}
          />
        ) : undefined
      }
      canvasBackgroundColor={backCanvas.containerStyle?.backgroundColor}
    >
      {customCss && <CustomCssGlobalStyle $customCss={customCss} />}
      <FrameInner>
        <Page
          customBackgroundColor={pageColor}
          customTextColor={pageFontColor}
          applicatorProp={layerMiddle.prop}
          showBorder={applicatorConfig?.layerMiddle.border?.isEnabled}
          noTranslucency={!applicatorConfig?.layerMiddle.backgroundTranslucency?.isEnabled}
          isSinglePage={isSinglePage}
          topPadding={topPadding}
          themeSprites={
            middleCanvas.sprites.length > 0 ? (
              <ThemeSprites
                sprites={middleCanvas.sprites}
                context={MIDDLE_CONTEXT}
                frameInset={MIDDLE_FRAME_INSET}
                media={BRANNAN_FRAMES_MEDIA}
              />
            ) : undefined
          }
          canvasBackgroundColor={middleCanvas.containerStyle?.backgroundColor}
        >
          {children}
        </Page>
      </FrameInner>
    </FrameOuter>
  );
};
